import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Button from '../common/Button';
import FormField, { Input } from '../common/FormField';
import { publicEstimateAPI } from '../../services/api';
import { formatQuantity } from '../../lib/quantity';

// The customer's page for an emailed estimate link. The customer is not a CROW
// user: no login, no app shell. Everything shown comes from the view the
// backend returns for this one token.

const STEPS = ['Estimate approved', 'Work in progress', 'Done — ready for pickup'];
const ON_HOLD = 'On hold — the shop will be in touch';
const CLOSED = 'This job was closed';

// The shop's currency comes with the amount to pay; narrowSymbol keeps CAD and
// USD both reading "$" so the lines and the payment never disagree on a symbol.
const moneyIn = (n, currency = 'usd') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: String(currency).toUpperCase(), currencyDisplay: 'narrowSymbol',
    }).format(Number(n || 0));
  } catch {
    return `$${Number(n || 0).toFixed(2)}`;
  }
};

const money = (n) => moneyIn(n);
const POLL_EVERY_MS = 2000;
const POLL_MAX_TRIES = 10;

// approved_at is the customer's wall-clock time with no offset; Date reads an
// offset-less ISO string as local time, so it shows exactly as recorded.
const localDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const TOO_MANY = 'Too many tries — please wait a minute and try again';

function errorMessage(err) {
  const status = err?.response?.status;
  if (status === 429) return TOO_MANY;
  if (status === 502) return err.response?.data?.detail || 'Payments are unavailable right now. Try again in a minute.';
  if (status === 409) return err.response?.data?.detail || 'This estimate changed — please review it again';
  if (status === 422) return 'Please check what you entered and try again';
  return 'Something went wrong. Please try again.';
}

function problemFor(err) {
  const status = err?.response?.status;
  if (status === 404) return { kind: 'invalid' };
  if (status === 410) return { kind: 'gone', ...(err.response.data || {}) };
  if (status === 429) return { kind: 'error', message: TOO_MANY };
  return { kind: 'error' };
}

function PhoneLink({ phone }) {
  return (
    <a href={`tel:${phone}`} className="text-accent underline underline-offset-2 inline-flex items-center min-h-touch">
      {phone}
    </a>
  );
}

function ClosedLine({ shopName, phone }) {
  return (
    <p className="text-body text-ink">
      This job was closed — contact {shopName || 'the shop'}
      {phone ? <> at <PhoneLink phone={phone} /></> : null}
    </p>
  );
}

function Tracker({ view }) {
  const { tracker, status_label: statusLabel, shop_name: shopName, shop_phone: phone } = view;
  if (!tracker) {
    // No approval is the customer's latest answer: report the job's status as
    // a plain line, never the approval steps.
    if (statusLabel === CLOSED) return <ClosedLine shopName={shopName} phone={phone} />;
    return (
      <p className="text-body text-ink">
        {statusLabel ? `${statusLabel}.` : 'The shop is working on your vehicle.'}
        {phone ? <> Questions? Call <PhoneLink phone={phone} /></> : null}
      </p>
    );
  }
  if (tracker === CLOSED) return <ClosedLine shopName={shopName} phone={phone} />;
  const onHold = tracker === ON_HOLD;
  const currentIndex = onHold ? -1 : STEPS.indexOf(tracker);
  return (
    <div>
      <ol className="space-y-1" aria-label="Job progress">
        {STEPS.map((label, i) => {
          const done = onHold ? i === 0 : i < currentIndex;
          const current = i === currentIndex;
          return (
            <li
              key={label}
              aria-current={current ? 'step' : undefined}
              className="flex items-center gap-3 py-2"
            >
              <span
                aria-hidden="true"
                className={`h-3.5 w-3.5 rounded-full flex-none ${
                  done ? 'bg-success' : current ? 'bg-warning ring-4 ring-warning-tint' : 'bg-border-strong'
                }`}
              />
              <span className={current ? 'font-semibold text-ink' : done ? 'text-ink' : 'text-ink-muted'}>{label}</span>
              {done && <span className="sr-only">(done)</span>}
              {current && <span className="sr-only">(current step)</span>}
            </li>
          );
        })}
      </ol>
      {onHold && (
        <p className="mt-2 text-body text-warning" role="status">{ON_HOLD}</p>
      )}
    </div>
  );
}

function LineItems({ view }) {
  const cur = view.pay?.currency;
  const money = (n) => moneyIn(n, cur);
  return (
    <div>
      {view.lines.filter((line) => !(line.labor_cost !== undefined && !Number(line.labor_cost))).map((line, i) => {
        const isLabour = line.labor_cost !== undefined;
        const detail = isLabour
          ? `${line.labor_hours} h × ${money(line.labor_rate)}/h`
          : [line.part_number, line.condition, line.quantity !== 1 ? `${formatQuantity(line.quantity)} × ${money(line.unit_price)}` : null]
              .filter(Boolean).join(' · ');
        return (
          <div key={i} className="flex justify-between gap-4 py-2 border-b border-border-default">
            <div>
              <div className="text-body text-ink">{line.name}</div>
              {detail && <div className="text-small text-ink-muted">{detail}</div>}
            </div>
            <div className="text-body text-ink tabular-nums">{money(isLabour ? line.labor_cost : line.line_total)}</div>
          </div>
        );
      })}
      {view.discount_amount > 0 && (
        <div className="flex justify-between py-2 border-b border-border-default text-body text-ink">
          <span>Discount</span><span className="tabular-nums">-{money(view.discount_amount)}</span>
        </div>
      )}
      {view.tax_amount > 0 && (
        <div className="flex justify-between py-2 border-b border-border-default text-body text-ink">
          <span>Tax</span><span className="tabular-nums">{money(view.tax_amount)}</span>
        </div>
      )}
      <div className="flex justify-between pt-3 text-section-title text-ink">
        <span>Total</span><span className="tabular-nums">{money(view.total)}</span>
      </div>
    </div>
  );
}

function Decision({ view, actions, canDecline = true }) {
  const [mode, setMode] = useState(null); // null | 'approve' | 'decline'
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const deposit = Number(view.deposit_preview) > 0 ? view.deposit_preview : null;

  useEffect(() => {
    if (mode === 'approve') document.getElementById('approver-name')?.focus();
    if (mode === 'decline') document.getElementById('decline-reason')?.focus();
  }, [mode]);

  const submit = async (action) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const next = action === 'approve'
        ? await actions.approve(name.trim(), view.total)
        : await actions.decline(reason.trim() || null, view.total);
      actions.done(next);
      // If paying fails now, the approval stands: the page shows "Deposit due"
      // with its own error and a "Pay deposit" button.
      if (action === 'approve' && next?.pay?.purpose === 'deposit') await actions.pay();
      else if (action === 'approve' && deposit && next?.state === 'approved') {
        actions.note('Approved. The shop will contact you about the deposit.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) { actions.fail(err); return; }
      setError(errorMessage(err));
      if (status === 409) actions.reload();
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-3">
      {mode === null && (
        <>
          <Button className="w-full" onClick={() => setMode('approve')}>
            {deposit ? `Approve and pay ${moneyIn(deposit)} deposit` : 'Approve estimate'}
          </Button>
          {canDecline && (
            <Button className="w-full" variant="danger" onClick={() => setMode('decline')}>Decline</Button>
          )}
        </>
      )}
      {mode === 'approve' && (
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) submit('approve'); }} className="space-y-3">
          <FormField label="Type your full name to approve" htmlFor="approver-name" required>
            <Input id="approver-name" value={name} onChange={(e) => setName(e.target.value)}
              autoComplete="name" maxLength={200} />
          </FormField>
          <Button type="submit" className="w-full" disabled={!name.trim()} loading={busy}>
            {deposit ? `Confirm and pay ${moneyIn(deposit)} deposit` : 'Confirm approval'}
          </Button>
          <Button type="button" variant="secondary" className="w-full" disabled={busy}
            onClick={() => { setMode(null); setError(''); }}>Back</Button>
        </form>
      )}
      {mode === 'decline' && (
        <form onSubmit={(e) => { e.preventDefault(); submit('decline'); }} className="space-y-3">
          <FormField label="Anything you'd like the shop to know?" htmlFor="decline-reason">
            <textarea id="decline-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} maxLength={1000}
              className="w-full px-3 py-2 rounded-control border border-border-strong bg-surface-card text-body text-ink focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />
          </FormField>
          <Button type="submit" variant="danger" className="w-full" loading={busy}>Confirm decline</Button>
          <Button type="button" variant="secondary" className="w-full" disabled={busy}
            onClick={() => { setMode(null); setError(''); }}>Back</Button>
        </form>
      )}
      {error && <p role="alert" className="text-body text-danger">{error}</p>}
    </div>
  );
}

function PayButton({ busy, onClick, children }) {
  return (
    <Button className="w-full" onClick={onClick} disabled={busy} loading={busy}>{children}</Button>
  );
}

// Everything about money once the customer has approved: what is due, what has
// been paid, and what happens next. The server decides every amount; this only
// shows them and asks.
function PaySection({ view, note, payment, payBusy, payError, onPay }) {
  const { pay } = view;
  const error = payError && <p role="alert" className="text-body text-danger">{payError}</p>;

  if (payment === 'processing') {
    return <p role="status" className="text-body text-ink">Processing your payment…</p>;
  }
  if (payment === 'received') {
    return <p role="status" className="text-body font-semibold text-success">Payment received — thank you</p>;
  }
  if (payment === 'unconfirmed') {
    return <p role="status" className="text-body text-ink">We're still confirming your payment. You can close this page.</p>;
  }
  if (view.paid_in_full) {
    return <p role="status" className="text-body font-semibold text-success">Paid in full — thank you</p>;
  }
  if (pay?.purpose === 'deposit') {
    return (
      <div className="space-y-3">
        <p role="status" className="p-3 rounded-control bg-warning-tint border border-warning-border text-body text-ink">
          {`Deposit due — ${moneyIn(pay.amount, pay.currency)}`}
        </p>
        <PayButton busy={payBusy} onClick={onPay}>Pay deposit</PayButton>
        {error}
      </div>
    );
  }
  if (pay?.purpose === 'balance') {
    return (
      <div className="space-y-3">
        <h2 className="text-section-title text-ink">Your car is ready</h2>
        <LineItems view={view} />
        {view.deposit_paid > 0 && (
          <div className="flex justify-between text-body text-ink">
            <span>Deposit paid</span>
            <span className="tabular-nums">-{moneyIn(view.deposit_paid, pay.currency)}</span>
          </div>
        )}
        <div className="flex justify-between text-section-title text-ink">
          <span>Balance</span><span className="tabular-nums">{moneyIn(pay.amount, pay.currency)}</span>
        </div>
        <PayButton busy={payBusy} onClick={onPay}>Pay {moneyIn(pay.amount, pay.currency)}</PayButton>
        {error}
      </div>
    );
  }
  if (view.pay_blocked === 'resave') {
    return <p role="status" className="text-body text-ink">The shop needs to update this invoice before you can pay online.</p>;
  }
  if (view.pay_at_shop) {
    return <p role="status" className="text-body text-ink">Pay at the shop when you pick up your car</p>;
  }
  if (note) return <p role="status" className="text-body text-ink">{note}</p>;
  return null;
}

function Shell({ shopName, children }) {
  return (
    <div className="min-h-screen bg-surface-page">
      <header className="bg-surface-card border-b border-border-default">
        <div className="max-w-xl mx-auto px-4 py-4">
          <h1 className="text-page-title font-display text-ink">{shopName || 'Estimate'}</h1>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-6">
        <div className="bg-surface-card rounded-card border border-border-default p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
}

function Notice({ shopName, children }) {
  return (
    <Shell shopName={shopName}>
      <div role="status" className="text-body text-ink space-y-2">{children}</div>
    </Shell>
  );
}

export default function EstimateApprovalPage() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [problem, setProblem] = useState(null); // {kind, ...}
  const [loading, setLoading] = useState(true);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState('');
  const [pollDone, setPollDone] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const payInFlight = useRef(false);
  const justPaid = new URLSearchParams(useLocation().search).get('paid') === '1';

  const load = useCallback(async () => {
    try {
      setView(await publicEstimateAPI.get(token));
      setProblem(null);
    } catch (err) {
      setProblem(problemFor(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Back from checkout the payment may not be recorded yet (the card network
  // tells us by webhook). Ask again every 2 s, at most 10 times.
  useEffect(() => {
    if (!justPaid || loading) return undefined;
    let tries = 0;
    let stopped = false;
    const settled = (v) => v?.paid_in_full || (!v?.pay && v?.amount_paid > 0);
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const next = await publicEstimateAPI.get(token);
        if (stopped) return;
        setView(next);
        if (settled(next)) { clearInterval(timer); setPollDone(true); return; }
      } catch {
        // keep trying; the last good view stays on screen
      }
      if (!stopped && tries >= POLL_MAX_TRIES) { clearInterval(timer); setPollDone(true); }
    }, POLL_EVERY_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [justPaid, loading, token]);

  // Coming back with the browser's Back button must not leave the button dead.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) { payInFlight.current = false; setPayBusy(false); } };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const startPay = useCallback(async () => {
    if (payInFlight.current) return;
    payInFlight.current = true;
    setPayBusy(true);
    setPayError('');
    try {
      const { url } = await publicEstimateAPI.pay(token);
      window.location.href = url; // stay disabled while the browser leaves
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) { setProblem(problemFor(err)); return; }
      setPayError(errorMessage(err));
      payInFlight.current = false;
      setPayBusy(false);
    }
  }, [token]);

  useEffect(() => {
    const original = document.title;
    return () => { document.title = original; };
  }, []);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    document.title = view?.shop_name ? `Estimate from ${view.shop_name}` : 'Your estimate';
  }, [view?.shop_name]);

  if (loading) {
    return <Notice><p>Loading your estimate…</p></Notice>;
  }

  if (problem?.kind === 'invalid') {
    return <Notice><p>This link isn't valid.</p></Notice>;
  }
  if (problem?.kind === 'gone') {
    const shop = problem.shop_name || 'the shop';
    return (
      <Notice shopName={problem.shop_name}>
        {problem.reason === 'replaced' ? (
          <p>This link was replaced — check your latest email from {shop}</p>
        ) : (
          <p>
            This link has expired — contact {shop}
            {problem.shop_phone ? <> at <PhoneLink phone={problem.shop_phone} /></> : null}
          </p>
        )}
      </Notice>
    );
  }
  if (problem?.kind === 'error' || !view) {
    return (
      <Shell>
        <div role="alert" className="space-y-3">
          <p className="text-body text-danger">{problem?.message || 'Something went wrong loading your estimate.'}</p>
          <Button onClick={() => { setLoading(true); load(); }}>Try again</Button>
        </div>
      </Shell>
    );
  }

  const { state } = view;
  const vehicleLine = [view.vehicle, view.license_plate, view.job_number].filter(Boolean).join(' · ');
  const showEstimate = state === 'open' || state === 'changed';
  let payment = null;
  if (justPaid && !showEstimate && state !== 'declined') {
    if (view.paid_in_full || (!view.pay && view.amount_paid > 0)) payment = 'received';
    else payment = pollDone ? 'unconfirmed' : 'processing';
  }
  const paySection = (
    <PaySection view={view} note={approvalNote} payment={payment} payBusy={payBusy} payError={payError} onPay={startPay} />
  );
  const actions = {
    approve: (name, total) => publicEstimateAPI.approve(token, name, total),
    decline: (reason, total) => publicEstimateAPI.decline(token, reason, total),
    done: setView,
    reload: load,
    fail: (err) => setProblem(problemFor(err)),
    pay: startPay,
    note: setApprovalNote,
  };

  return (
    <Shell shopName={view.shop_name}>
      {vehicleLine && <p className="text-small text-ink-muted mb-4">{vehicleLine}</p>}

      {state === 'changed' && (
        <div role="alert" className="mb-4 p-3 rounded-control bg-warning-tint border border-warning-border text-body text-ink">
          This estimate changed since you approved it
        </div>
      )}

      {showEstimate && (
        <>
          <LineItems view={view} />
          {view.notes && (
            <p className="mt-4 text-small text-ink-muted">Mechanic's note: {view.notes}</p>
          )}
          <Decision view={view} actions={actions} />
          {view.shop_phone && (
            <p className="mt-4 text-small text-ink-muted">Questions? Call <PhoneLink phone={view.shop_phone} /></p>
          )}
        </>
      )}

      {state === 'approved' && (
        <div className="space-y-4">
          <Tracker view={view} />
          <p className="text-body text-ink">
            Approved by {view.approved_by}{localDateTime(view.approved_at) ? `, ${localDateTime(view.approved_at)}` : ''}
          </p>
          <p className="text-body text-ink-muted">Approved total: <span className="tabular-nums">{money(view.total)}</span></p>
          {paySection}
        </div>
      )}

      {state === 'declined' && (
        <>
          <p className="text-body text-ink">
            You declined this estimate.
            {view.shop_phone ? <> Questions? Call <PhoneLink phone={view.shop_phone} /></> : null}
          </p>
          <p className="mt-2 mb-4 text-body text-ink-muted">Changed your mind? Here is the estimate again.</p>
          <LineItems view={view} />
          <Decision view={view} actions={actions} canDecline={false} />
        </>
      )}

      {state === 'status_only' && (
        <div className="space-y-4">
          <Tracker view={view} />
          {paySection}
        </div>
      )}
    </Shell>
  );
}
